// JQ4-45 · US-AUDIT-005 — SuperAdminAuditLogPage (Super Admin)
//
// Audit log viewer for super_admin role.
// Adds an org selector before showing the filter bar and results.
// Uses GET /super-admin/organizations/:orgId/audit-log.
//
// R1 SCOPE GUARD (ADR-2 / design R1):
//   The super-admin actor picker (cross-org user dropdown) is OUT OF SCOPE.
//   No cross-org user-list endpoint exists. The actor-userId filter accepts
//   a raw UUID input or is hidden when not needed. If/when JQ4-XX adds a
//   per-org user-list endpoint, wire it here.
//
// Spec refs: R-RBAC-04, R-FILTER-01 to R-FILTER-06, R-LIST-01 to R-LIST-06,
//            R-DETAIL-01 to R-DETAIL-04, R-EMPTY-01 to R-EMPTY-03.

import { listOrgAuditLog, listOrganizations } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { AuditCategory, ListAuditLogQuery, OrganizationResponse } from '@jq4race/shared';
import { Skeleton } from '@jq4race/ui';
import { TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useCursorPagination } from '../../../../shared/hooks/useCursorPagination';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { isValidUuid } from '../../lib/isValidUuid';
import { AuditLogFilterBar, type AuditLogFilters } from '../components/AuditLogFilterBar';
import { AuditLogTable } from '../components/AuditLogTable';

const INITIAL_FILTERS: AuditLogFilters = {
  category: '',
  from: '',
  to: '',
  // R1 SCOPE GUARD: actor picker hidden for super-admin (no cross-org user-list endpoint).
  // actorUserId remains in the filter object for structural parity but the
  // filter bar passes it through as a raw UUID input, same as org-admin.
  actorUserId: '',
};

const PAGE_LIMIT = 25;

export function SuperAdminAuditLogPage() {
  const { t } = useTranslation('audit-log');

  // ── Org selector state ────────────────────────────────────────────────────
  const [orgs, setOrgs] = useState<OrganizationResponse[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  const loadOrgs = useCallback(async () => {
    setOrgsLoading(true);
    setOrgsError(null);
    try {
      const res = await listOrganizations();
      setOrgs(res.organizations);
    } catch (e) {
      setOrgsError(translateApiError(e, t, { fallback: t('orgSelector.loadFailed') }));
    } finally {
      setOrgsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadOrgs();
  }, [loadOrgs]);

  // ── Filter state ─────────────────────────────────────────────────────────
  const [filters, setFilters] = useState<AuditLogFilters>(INITIAL_FILTERS);

  // R-LIST-06: queryKey includes org + all filters — changing any resets pagination
  const queryKey = useMemo(
    () => [
      'audit-log',
      'super-admin',
      selectedOrgId || null,
      filters.category || null,
      filters.from || null,
      filters.to || null,
      // Only key on a valid UUID so partial actor input never triggers a 400.
      isValidUuid(filters.actorUserId) ? filters.actorUserId : null,
    ],
    [filters, selectedOrgId],
  );

  const fetchPage = useMemo(
    () => (cursor: string | null) => {
      const params: ListAuditLogQuery = {
        cursor: cursor ?? undefined,
        limit: PAGE_LIMIT,
      };
      if (filters.category) params.category = filters.category as AuditCategory;
      if (filters.from) params.from = `${filters.from}T00:00:00Z`;
      // Inclusive end-of-day with sub-second window so forensic events are not dropped.
      if (filters.to) params.to = `${filters.to}T23:59:59.999Z`;
      if (filters.actorUserId && isValidUuid(filters.actorUserId))
        params.actorUserId = filters.actorUserId;
      return listOrgAuditLog(selectedOrgId, params);
    },
    [filters, selectedOrgId],
  );

  // Disable query until an org is selected (ADR-5)
  const { items, hasMore, isLoading, isFetchingNextPage, fetchNextPage, error } =
    useCursorPagination({
      queryKey,
      fetchPage,
      enabled: !!selectedOrgId,
    });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.superAdminDescription')}
      />

      {/* Org selector — required for super_admin (R-FILTER-06) */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="super-admin-org-select"
          className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
        >
          {t('orgSelector.label')}
        </label>
        {orgsLoading ? (
          <Skeleton className="h-9 w-[280px]" />
        ) : orgsError ? (
          <p className="text-[13px] text-jq-danger">{orgsError}</p>
        ) : (
          <select
            id="super-admin-org-select"
            value={selectedOrgId}
            onChange={(e) => {
              setSelectedOrgId(e.target.value);
              setFilters(INITIAL_FILTERS); // reset filters on org change
            }}
            className="h-9 w-[280px] rounded-md border border-jq-sand/70 bg-jq-surface-0 px-3 text-[13px] text-jq-charcoal focus:border-jq-navy/40 focus:outline-none focus:ring-1 focus:ring-jq-navy/20"
          >
            <option value="">{t('orgSelector.placeholder')}</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Only show filter bar and results once an org is selected */}
      {selectedOrgId ? (
        <>
          {/* Error banner */}
          {error && <ErrorBanner message={translateApiError(error, t)} />}

          {/* Filter bar */}
          <AuditLogFilterBar filters={filters} onFiltersChange={setFilters} />

          {/* Results table */}
          <AuditLogTable
            items={items}
            isLoading={isLoading}
            hasMore={hasMore}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        </>
      ) : (
        !orgsLoading && <p className="text-[13px] text-jq-graphite">{t('orgSelector.hint')}</p>
      )}
    </div>
  );
}

// ── Error banner ──────────────────────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  const { t } = useTranslation('audit-log');
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('errors.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
